import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SERVICE_OPTIONS, HEARD_OPTIONS, COMPANY } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, CheckCircle2, Phone } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", email: "", phone: "", service: "", message: "", heard: "" };

export const LeadForm = ({ source = "website", compact = false, zip = null }) => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", { ...form, heard_about: form.heard, zip, source });
      setDone(true);
      setForm(empty);
      toast.success("Thank you! A member of our team will contact you shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit. Please call us.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="lead-form-success" className="text-center py-10 px-6">
        <CheckCircle2 className="w-16 h-16 text-primary mx-auto mb-4" />
        <h3 className="font-heading text-3xl text-primary mb-2">Request Received</h3>
        <p className="text-muted-foreground text-lg mb-6">
          A friendly member of our team will reach out soon to plan the right care for you and your family.
        </p>
        <a href={COMPANY.phoneHref} className="inline-flex items-center gap-2 text-primary font-semibold text-lg">
          <Phone className="w-5 h-5" /> Or call us now: {COMPANY.phone}
        </a>
      </div>
    );
  }

  return (
    <form data-testid="lead-form" onSubmit={submit} className="space-y-4">
      <div>
        <Label htmlFor="lf-name" className="text-base">Full Name *</Label>
        <Input id="lf-name" data-testid="lead-name-input" required value={form.name} onChange={set("name")}
          className="h-14 text-lg mt-1.5 bg-white" placeholder="Jane Doe" />
      </div>
      <div className={compact ? "" : "grid sm:grid-cols-2 gap-4"}>
        <div>
          <Label htmlFor="lf-email" className="text-base">Email *</Label>
          <Input id="lf-email" type="email" data-testid="lead-email-input" required value={form.email} onChange={set("email")}
            className="h-14 text-lg mt-1.5 bg-white" placeholder="jane@email.com" />
        </div>
        <div>
          <Label htmlFor="lf-phone" className="text-base">Telephone *</Label>
          <Input id="lf-phone" data-testid="lead-phone-input" required value={form.phone} onChange={set("phone")}
            className="h-14 text-lg mt-1.5 bg-white" placeholder="(703) 555-0123" />
        </div>
      </div>
      <div>
        <Label className="text-base">How can we help? *</Label>
        <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
          <SelectTrigger data-testid="lead-service-select" className="h-14 text-lg mt-1.5 bg-white">
            <SelectValue placeholder="Select an option" />
          </SelectTrigger>
          <SelectContent>
            {SERVICE_OPTIONS.map((o) => (
              <SelectItem key={o} value={o} className="text-base">{o}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-base">How did you hear about us?</Label>
        <Select value={form.heard} onValueChange={(v) => setForm((f) => ({ ...f, heard: v }))}>
          <SelectTrigger data-testid="lead-heard-select" className="h-14 text-lg mt-1.5 bg-white">
            <SelectValue placeholder="Select an option" />
          </SelectTrigger>
          <SelectContent>
            {HEARD_OPTIONS.map((o) => (
              <SelectItem key={o} value={o} className="text-base">{o}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="lf-msg" className="text-base">Message (optional)</Label>
        <Textarea id="lf-msg" data-testid="lead-message-input" value={form.message} onChange={set("message")}
          className="text-lg mt-1.5 bg-white min-h-[100px]" placeholder="Tell us a little about your needs..." />
      </div>
      <button
        type="submit"
        data-testid="lead-submit-btn"
        disabled={loading}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-accent hover:bg-accent/90 text-accent-foreground h-14 text-lg font-bold transition-colors disabled:opacity-60"
      >
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
        Request Your Free Consultation
      </button>
      <p className="text-sm text-muted-foreground text-center">
        Prefer to talk? Call us at <a href={COMPANY.phoneHref} className="text-primary font-semibold">{COMPANY.phone}</a>
      </p>
    </form>
  );
};
